import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Landmark, Plus, QrCode, Smartphone, Star, Trash2 } from 'lucide-react';
import {
  ACCOUNT_TYPE_LABELS,
  AccountType,
  BankAccountKind,
  UPLOAD,
  esBilletera,
} from '@kick/types';
import { getErrorDetails, getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  useCuentasDomiciliario,
  useEliminarCuenta,
  useGuardarCuenta,
  useSubirQr,
  type CuentaDomiciliario,
} from '@/lib/queries';
import { Boton, Campo, Cargando, ConfirmarModal, Entrada, Modal, Seleccion } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const TIPOS: Record<string, { nombre: string; icono: typeof Smartphone; billetera: boolean }> =
  Object.fromEntries(
    Object.values(AccountType).map((tipo) => [
      tipo,
      {
        nombre: ACCOUNT_TYPE_LABELS[tipo],
        icono: esBilletera(tipo) ? Smartphone : Landmark,
        billetera: esBilletera(tipo),
      },
    ]),
  );

/**
 * Cuentas donde el domiciliario recibe transferencias.
 *
 * Las carga administración mientras la app de la fase 9 no exista; cuando
 * llegue, el domiciliario las gestiona desde su teléfono con estos mismos
 * endpoints.
 */
export function CuentasDomiciliario({
  courierId,
  nombre,
}: {
  courierId: string;
  nombre: string;
}) {
  const cuentas = useCuentasDomiciliario(courierId);
  const [editando, setEditando] = useState<CuentaDomiciliario | null>(null);
  const [creando, setCreando] = useState(false);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-kick-gray">
          A estas cuentas transfieren los clientes que no pagan en efectivo.
        </p>
        <Boton
          variante="secundario"
          className="px-3 py-1.5 text-xs"
          icono={<Plus className="h-3.5 w-3.5" />}
          onClick={() => setCreando(true)}
        >
          Agregar cuenta
        </Boton>
      </div>

      {cuentas.isLoading && <Cargando />}

      {cuentas.data?.length === 0 && (
        <p className="mt-3 rounded-kick bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
          Sin cuentas registradas, {nombre.split(' ')[0]} solo puede cobrar en efectivo.
        </p>
      )}

      <ul className="mt-3 space-y-2">
        {cuentas.data?.map((cuenta) => (
          <Fila
            key={cuenta.id}
            cuenta={cuenta}
            courierId={courierId}
            onEditar={() => setEditando(cuenta)}
          />
        ))}
      </ul>

      <ModalCuenta
        abierto={creando || !!editando}
        cuenta={editando}
        courierId={courierId}
        nombre={nombre}
        onCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
      />
    </div>
  );
}

function Fila({
  cuenta,
  courierId,
  onEditar,
}: {
  cuenta: CuentaDomiciliario;
  courierId: string;
  onEditar: () => void;
}) {
  const avisos = useAvisos();
  const eliminar = useEliminarCuenta(courierId);
  const subirQr = useSubirQr(courierId);
  const entrada = useRef<HTMLInputElement>(null);
  const [confirmando, setConfirmando] = useState(false);

  const tipo = TIPOS[cuenta.type];
  const Icono = tipo?.icono ?? Landmark;

  const elegirQr = (archivo: File | undefined): void => {
    if (!archivo) return;
    if (archivo.size > UPLOAD.maxImageBytes) {
      avisos.error(`La imagen no puede superar ${UPLOAD.maxImageBytes / 1024 / 1024} MB`);
      return;
    }
    subirQr.mutate(
      { id: cuenta.id, archivo },
      {
        onSuccess: () => avisos.exito('QR actualizado'),
        onError: (error) => avisos.error(getErrorMessage(error)),
      },
    );
  };

  return (
    <li className="flex flex-wrap items-center gap-3 rounded-kick border border-kick-gray/20 p-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-kick bg-kick-light text-kick-gray">
        <Icono className="h-4 w-4" />
      </span>

      <button type="button" onClick={onEditar} className="min-w-0 flex-1 text-left">
        <span className="flex items-center gap-1.5 font-semibold text-kick-ink">
          {tipo?.nombre ?? cuenta.type}
          {cuenta.bankName && ` · ${cuenta.bankName}`}
          {cuenta.isDefault && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
        </span>
        <span className="block font-mono text-sm text-kick-gray">{cuenta.accountNumber}</span>
        <span className="block text-xs text-kick-gray">
          {cuenta.holderName}
          {cuenta.accountKind && ` · ${cuenta.accountKind.toLowerCase()}`}
        </span>
      </button>

      {cuenta.qrUrl ? (
        <img
          src={cuenta.qrUrl}
          alt="QR de la cuenta"
          className="h-14 w-14 rounded border border-kick-gray/20 object-contain"
        />
      ) : (
        <span className="grid h-14 w-14 place-items-center rounded border border-dashed border-kick-gray/30 text-kick-gray/40">
          <QrCode className="h-5 w-5" />
        </span>
      )}

      <input
        ref={entrada}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => elegirQr(e.target.files?.[0])}
      />

      <Boton
        variante="secundario"
        className="px-2.5 py-1.5 text-xs"
        icono={<ImagePlus className="h-3.5 w-3.5" />}
        cargando={subirQr.isPending}
        onClick={() => entrada.current?.click()}
      >
        {cuenta.qrUrl ? 'Cambiar QR' : 'Subir QR'}
      </Boton>

      <button
        type="button"
        onClick={() => setConfirmando(true)}
        aria-label="Eliminar la cuenta"
        className="rounded-kick p-2 text-kick-gray transition hover:bg-red-50 hover:text-red-600"
      >
        <Trash2 className="h-4 w-4" />
      </button>

      <ConfirmarModal
        abierto={confirmando}
        titulo="Eliminar la cuenta"
        mensaje="Los clientes dejarán de verla al transferir. Los pedidos ya pagados no cambian."
        cargando={eliminar.isPending}
        onCancelar={() => setConfirmando(false)}
        onConfirmar={() =>
          eliminar.mutate(cuenta.id, {
            onSuccess: () => {
              setConfirmando(false);
              avisos.exito('Cuenta eliminada');
            },
            onError: (error) => avisos.error(getErrorMessage(error)),
          })
        }
      />
    </li>
  );
}

function ModalCuenta({
  abierto,
  cuenta,
  courierId,
  nombre,
  onCerrar,
}: {
  abierto: boolean;
  cuenta: CuentaDomiciliario | null;
  courierId: string;
  nombre: string;
  onCerrar: () => void;
}) {
  const avisos = useAvisos();
  const guardar = useGuardarCuenta(courierId);

  const [datos, setDatos] = useState({
    type: AccountType.NEQUI as string,
    bankName: '',
    accountKind: BankAccountKind.AHORROS as string,
    accountNumber: '',
    holderName: '',
    holderDocument: '',
    isDefault: false,
  });

  useEffect(() => {
    if (!abierto) return;
    setDatos(
      cuenta
        ? {
            type: cuenta.type,
            bankName: cuenta.bankName ?? '',
            accountKind: cuenta.accountKind ?? BankAccountKind.AHORROS,
            accountNumber: cuenta.accountNumber,
            holderName: cuenta.holderName,
            holderDocument: cuenta.holderDocument ?? '',
            isDefault: cuenta.isDefault,
          }
        : {
            type: AccountType.NEQUI,
            bankName: '',
            accountKind: BankAccountKind.AHORROS,
            accountNumber: '',
            holderName: nombre,
            holderDocument: '',
            isDefault: false,
          },
    );
  }, [abierto, cuenta, nombre]);

  const esBilletera = TIPOS[datos.type]?.billetera ?? false;
  const errores = guardar.error ? getErrorDetails(guardar.error) : {};

  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo={cuenta ? 'Editar cuenta' : 'Nueva cuenta'}
      descripcion="El cliente ve estos datos exactamente así antes de transferir."
      ancho="sm"
    >
      <div className="p-5">
        <Campo etiqueta="Dónde recibe" requerido>
          <Seleccion
            value={datos.type}
            onChange={(e) => setDatos({ ...datos, type: e.target.value })}
          >
            {Object.entries(TIPOS).map(([valor, t]) => (
              <option key={valor} value={valor}>
                {t.nombre}
              </option>
            ))}
          </Seleccion>
        </Campo>

        {datos.type === AccountType.OTRO_BANCO && (
          <Campo etiqueta="Nombre del banco" requerido error={errores.bankName?.[0]} className="mt-4">
            <Entrada
              value={datos.bankName}
              onChange={(e) => setDatos({ ...datos, bankName: e.target.value })}
              placeholder="Davivienda"
            />
          </Campo>
        )}

        {!esBilletera && (
          <Campo etiqueta="Tipo de cuenta" requerido className="mt-4">
            <Seleccion
              value={datos.accountKind}
              onChange={(e) => setDatos({ ...datos, accountKind: e.target.value })}
            >
              <option value={BankAccountKind.AHORROS}>Ahorros</option>
              <option value={BankAccountKind.CORRIENTE}>Corriente</option>
            </Seleccion>
          </Campo>
        )}

        <Campo
          etiqueta={esBilletera ? 'Número de celular' : 'Número de cuenta'}
          requerido
          error={errores.accountNumber?.[0]}
          className="mt-4"
        >
          <Entrada
            value={datos.accountNumber}
            onChange={(e) => setDatos({ ...datos, accountNumber: e.target.value })}
            placeholder={esBilletera ? '3001234567' : '12345678901'}
            inputMode="numeric"
          />
        </Campo>

        <Campo
          etiqueta="A nombre de"
          requerido
          ayuda="Como aparece en el banco; el cliente lo verá"
          error={errores.holderName?.[0]}
          className="mt-4"
        >
          <Entrada
            value={datos.holderName}
            onChange={(e) => setDatos({ ...datos, holderName: e.target.value })}
          />
        </Campo>

        <Campo etiqueta="Documento del titular" ayuda="Opcional" className="mt-4">
          <Entrada
            value={datos.holderDocument}
            onChange={(e) => setDatos({ ...datos, holderDocument: e.target.value })}
          />
        </Campo>

        <label className="mt-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={datos.isDefault}
            onChange={(e) => setDatos({ ...datos, isDefault: e.target.checked })}
            className="h-4 w-4 accent-kick-red"
          />
          <span className="text-sm text-kick-ink">Mostrarla primero al cliente</span>
        </label>

        <div className="mt-6 flex justify-end gap-2">
          <Boton variante="secundario" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            cargando={guardar.isPending}
            disabled={datos.accountNumber.length < 6 || datos.holderName.length < 3}
            onClick={() =>
              guardar.mutate(
                {
                  ...(cuenta ? { id: cuenta.id } : {}),
                  type: datos.type,
                  bankName: datos.bankName || undefined,
                  // Las billeteras no llevan tipo de cuenta.
                  accountKind: esBilletera ? null : datos.accountKind,
                  accountNumber: datos.accountNumber.trim(),
                  holderName: datos.holderName.trim(),
                  holderDocument: datos.holderDocument.trim() || undefined,
                  isDefault: datos.isDefault,
                  isActive: true,
                },
                {
                  onSuccess: () => {
                    onCerrar();
                    avisos.exito(cuenta ? 'Cuenta actualizada' : 'Cuenta agregada');
                  },
                  onError: (error) => avisos.error(getErrorMessage(error)),
                },
              )
            }
          >
            Guardar
          </Boton>
        </div>
      </div>
    </Modal>
  );
}

/** Etiqueta compacta del estado de las cuentas, para el listado. */
export function ResumenCuentas({ courierId }: { courierId: string }) {
  const cuentas = useCuentasDomiciliario(courierId);
  const total = cuentas.data?.length ?? 0;

  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-semibold',
        total > 0 ? 'bg-kick-light text-kick-gray' : 'bg-amber-100 text-amber-800',
      )}
    >
      {total > 0 ? `${total} cuenta${total === 1 ? '' : 's'}` : 'sin cuentas'}
    </span>
  );
}
